import {
  useEquipmentCatalogue,
  useOrganisationEquipmentTypes,
  useResources,
  useSitesWithMembers,
} from '@my/directory-api/hooks';
import type { Resource, ResourceTypeValue } from '@my/directory-api';
import type { ServiceTag } from '@my/tms-api';
import { SelectInput, Switch, TextInput } from '@my/ui';
import { Check, Trash2, X } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CertificationsSection } from './CertificationsSection';
import type { ResourceDraft } from './lib/resourceDraft';

const TYPE_ORDER: ResourceTypeValue[] = ['Driver', 'Truck', 'Rigid', 'Trailer', 'Other'];

interface ResourceEditorProps {
  draft: ResourceDraft;
  isNew: boolean;
  organisationId: string;
  /**
   * The persisted resource (with its certifications). Required for the
   * cert sub-panel; passed through unchanged from the parent's row data
   * so edits to the panel see fresh server state via React Query
   * invalidation. Undefined while in `isNew` mode (the cert panel is
   * suppressed there — operators add certs after the resource exists).
   */
  resource?: Resource;
  /**
   * Org-wide service-tag library, filtered to tags whose
   * `applyToServiceTypes` opts into the Resources area (or is null /
   * applies-to-all). Operators select from this list — there is no
   * inline-create on this page; new tags are added in Central
   * Configuration. Empty array is fine — the picker shows a hint
   * pointing at Central Config.
   */
  serviceTags?: ServiceTag[];
  onChange: (next: ResourceDraft) => void;
  onSave: () => void;
  onCancel: () => void;
  /** Hidden when undefined (e.g. on the +Add row). */
  onDelete?: () => void;
  isBusy: boolean;
}

/**
 * Inline editor for a single Resource row. Type-conditional fields:
 *
 *   - Mobile Number   → Driver / Truck / Rigid only.
 *   - Email           → Driver only.
 *   - Equipment Type  → Trailer / Rigid only, filtered to the org's
 *                       opted-in catalogue selection of the matching class.
 *   - Reference label → varies by type (Licence Number for Driver,
 *                       Registration for Truck / Rigid, Trailer Number
 *                       for Trailer, Reference for Other).
 *
 * Type is locked once persisted — operators can't reclassify a driver
 * to a truck after the fact (the field rules wouldn't survive). To
 * reclassify, soft-delete and recreate.
 *
 * Certifications are managed in a separate panel below this editor —
 * the cert grid + attachment upload are wave 3.
 */
export function ResourceEditor({
  draft,
  isNew,
  organisationId,
  resource,
  serviceTags,
  onChange,
  onSave,
  onCancel,
  onDelete,
  isBusy,
}: ResourceEditorProps) {
  // Same multi-namespace pattern as ResourcesPage: `resources` first,
  // `common` for shared action verbs (Save / Cancel / Delete) and state
  // labels (Active / Inactive). Cross-namespace lookups use the
  // `common:…` prefix so they're easy to grep.
  const { t } = useTranslation(['resources', 'common']);

  // Pull the org's opted-in equipment types + the static catalogue so we
  // can render labels (catalogue key → display label). Both are cached
  // aggressively in React Query so re-opening the editor is instant.
  const equipmentTypesQuery = useOrganisationEquipmentTypes(organisationId);
  const catalogueQuery = useEquipmentCatalogue();

  // Default-vehicle options — this org's trucks and rigids, name-sorted.
  const resourcesQuery = useResources(organisationId);
  const vehicleOptions = useMemo(
    () =>
      (resourcesQuery.data ?? [])
        .filter((r) => r.type === 'Truck' || r.type === 'Rigid')
        .map((r) => ({ value: r.id, label: r.displayName || r.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [resourcesQuery.data],
  );

  // Home depot options — the org's sites, name-sorted. Applies to every type.
  const sitesQuery = useSitesWithMembers(organisationId);
  const siteOptions = useMemo(
    () =>
      (sitesQuery.data ?? [])
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((s) => ({ value: s.id, label: s.name })),
    [sitesQuery.data],
  );

  const equipmentLabelByKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const cls of catalogueQuery.data?.classes ?? []) {
      for (const group of cls.groups) {
        for (const type of group.types) map.set(type.key, type.label);
      }
    }
    return map;
  }, [catalogueQuery.data]);

  // Filter the org's equipment selection to the *class* that matches the
  // current resource type. Driver / Truck / Other resources don't get a
  // picker at all.
  const equipmentOptions = useMemo(() => {
    const expectedClass =
      draft.type === 'Trailer' ? 'Trailer' : draft.type === 'Rigid' ? 'Rigid' : null;
    if (expectedClass === null) return [];
    const rows = (equipmentTypesQuery.data ?? []).filter((r) => r.class === expectedClass);
    return rows.map((r) => ({
      id: r.id,
      label: equipmentLabelByKey.get(r.catalogueKey) ?? r.catalogueKey,
    }));
  }, [draft.type, equipmentTypesQuery.data, equipmentLabelByKey]);

  // Pre-compute label dictionaries (typed-keys i18n won't accept template
  // literals; same pattern as ResourcesPage).
  const typeLabels: Record<ResourceTypeValue, string> = {
    Driver: t('types.driver'),
    Truck: t('types.truck'),
    Rigid: t('types.rigid'),
    Trailer: t('types.trailer'),
    Other: t('types.other'),
  };
  const referenceLabels: Record<ResourceTypeValue, string> = {
    Driver: t('form.referenceLabel.driver'),
    Truck: t('form.referenceLabel.truck'),
    Rigid: t('form.referenceLabel.rigid'),
    Trailer: t('form.referenceLabel.trailer'),
    Other: t('form.referenceLabel.other'),
  };

  // ── Type-shape helpers ─────────────────────────────────────────────────

  const hasMobile = (t: ResourceTypeValue) => t === 'Driver' || t === 'Truck' || t === 'Rigid';
  const hasEmail = (t: ResourceTypeValue) => t === 'Driver';
  const hasEquipment = (t: ResourceTypeValue) => t === 'Trailer' || t === 'Rigid';

  const setField = <K extends keyof ResourceDraft>(key: K, value: ResourceDraft[K]) =>
    onChange({ ...draft, [key]: value });

  const handleTypeSelect = (type: ResourceTypeValue) => {
    if (!isNew) return; // type is locked once persisted
    // Clear fields that aren't valid for the new type. Operators are
    // free to re-enter; we just don't want to ship stale state to the BE
    // which would (correctly) reject it.
    onChange({
      ...draft,
      type,
      mobileNumber: hasMobile(type) ? draft.mobileNumber : '',
      email: hasEmail(type) ? draft.email : '',
      organisationEquipmentTypeId: hasEquipment(type) ? draft.organisationEquipmentTypeId : '',
      defaultVehicleResourceId: type === 'Driver' ? draft.defaultVehicleResourceId : '',
    });
  };

  const isValid = !!draft.type && draft.name.trim().length > 0;

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
      {/* ── Type chip row (locked on edit) ─────────────────────────────── */}
      <div className="space-y-1">
        <p className="text-xs font-medium text-slate-600">{t('table.type')}</p>
        <div className="flex flex-wrap gap-1.5">
          {TYPE_ORDER.map((type) => {
            const active = draft.type === type;
            return (
              <button
                key={type}
                type="button"
                onClick={() => handleTypeSelect(type)}
                disabled={!isNew || isBusy}
                className={chipClass(active, !isNew)}
              >
                {typeLabels[type]}
              </button>
            );
          })}
        </div>
        {isNew && !draft.type && (
          <p className="text-[10px] italic text-slate-400">{t('form.selectType')}</p>
        )}
      </div>

      {/* Body — only renders once a type is picked. */}
      {draft.type && (
        <>
          {/* Row 1: Name + Display Name */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
            <TextInput
              label={requiredLabel(t('form.name'))}
              name="name"
              value={draft.name}
              maxLength={256}
              onChange={(e) => setField('name', e.target.value)}
              disabled={isBusy}
            />
            <TextInput
              label={t('form.displayName')}
              name="displayName"
              value={draft.displayName}
              maxLength={128}
              onChange={(e) => setField('displayName', e.target.value)}
              disabled={isBusy}
            />
          </div>

          {/* Row 2: Reference + Mobile (per-type) */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
            <TextInput
              label={referenceLabels[draft.type]}
              name="reference"
              value={draft.reference}
              maxLength={64}
              onChange={(e) => setField('reference', e.target.value)}
              disabled={isBusy}
            />
            {hasMobile(draft.type) && (
              <TextInput
                label={t('form.mobile')}
                name="mobileNumber"
                value={draft.mobileNumber}
                maxLength={32}
                placeholder="+44 7700 900111"
                onChange={(e) => setField('mobileNumber', e.target.value)}
                disabled={isBusy}
              />
            )}
          </div>

          {/* Row 3: Email (driver only) */}
          {hasEmail(draft.type) && (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
              <TextInput
                label={t('form.email')}
                name="email"
                type="email"
                value={draft.email}
                maxLength={320}
                onChange={(e) => setField('email', e.target.value)}
                disabled={isBusy}
              />
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
            <SelectInput
              label={t('form.depot')}
              name="homeSiteId"
              value={draft.homeSiteId}
              disabled={isBusy}
              options={[{ value: '', label: t('form.noDepot') }, ...siteOptions]}
              onChange={(e) => setField('homeSiteId', e.target.value)}
            />
            {/* Default vehicle — drivers only. A standing assignment, not a
                per-job one; the planner can still override it on the day. */}
            {draft.type === 'Driver' && (
              <SelectInput
                label={t('form.defaultVehicle')}
                name="defaultVehicleResourceId"
                value={draft.defaultVehicleResourceId}
                disabled={isBusy}
                options={[{ value: '', label: t('form.noDefaultVehicle') }, ...vehicleOptions]}
                onChange={(e) => setField('defaultVehicleResourceId', e.target.value)}
              />
            )}
            <Switch
              label="Status"
              name="status"
              checked={draft.status === 'Active'}
              enabledLabel={t('common:states.active')}
              disabledLabel={t('common:states.inactive')}
              disabled={isBusy}
              onChange={(e) => setField('status', e.currentTarget.checked ? 'Active' : 'Inactive')}
            />
          </div>

          {/* Equipment type — trailer / rigid only */}
          {hasEquipment(draft.type) && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-slate-600">{t('form.equipmentType')}</p>
              {equipmentOptions.length === 0 ? (
                <p className="text-[10px] italic text-slate-400">
                  {t('form.noEquipment', { type: typeLabels[draft.type] })}
                </p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {equipmentOptions.map((eq) => {
                    const active = draft.organisationEquipmentTypeId === eq.id;
                    return (
                      <button
                        key={eq.id}
                        type="button"
                        onClick={() => setField('organisationEquipmentTypeId', active ? '' : eq.id)}
                        disabled={isBusy}
                        className={chipClass(active)}
                      >
                        {eq.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Certifications panel — edit-mode only. Live-updates: every
              cert toggle / field change / attachment upload fires its
              own API call and the resource list refetches via React
              Query invalidation. Suppressed in "new" mode because the
              resource doesn't have an id yet to attach certs to;
              operators add certs after the resource exists. */}
          {!isNew && resource && (
            <CertificationsSection
              resource={resource}
              organisationId={organisationId}
              serviceTags={serviceTags}
              isBusy={isBusy}
            />
          )}
        </>
      )}

      {/* ── Actions ───────────────────────────────────────────────────── */}
      {/* Standard action-row layout used across the suite — Delete at
          the left, Cancel + Save clustered at the right via
          `justify-between`. */}
      <div className="flex items-center justify-between gap-2 pt-1">
        <div>
          {!isNew && onDelete && (
            <button
              type="button"
              aria-label={t('common:actions.delete')}
              onClick={onDelete}
              disabled={isBusy}
              className="flex h-7 w-7 items-center justify-center rounded-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label={t('common:actions.cancel')}
            onClick={onCancel}
            disabled={isBusy}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-200 disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            aria-label={t('common:actions.save')}
            onClick={onSave}
            disabled={isBusy || !isValid}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-green-600 hover:bg-green-50 disabled:opacity-50"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

/** Floating-label content with a trailing red asterisk for required fields. */
function requiredLabel(text: string) {
  return (
    <>
      {text} <span className="text-red-500">*</span>
    </>
  );
}

/**
 * Shared chip styling for the type + equipment chip rows. `locked` makes
 * the cursor a default (not "this is a no-op button"), which matches the
 * "Type is locked after first save" UX.
 */
function chipClass(active: boolean, locked = false): string {
  const base =
    'inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors disabled:opacity-50';
  const palette = active
    ? 'border-slate-700 bg-slate-700 text-white'
    : 'border-slate-300 bg-white text-slate-600 hover:border-slate-500 hover:text-slate-800';
  return `${base} ${palette}${locked ? ' cursor-default' : ''}`;
}
